import { TextareaThemeUI as BrandTextarea } from '@jotta/ui/themeui'
import type { BoxProps } from '@jotta/ui/Box'
import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import { makeAutoObservable } from 'mobx'
import { observer } from 'mobx-react-lite'
import type { ForwardedRef } from 'react'

import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { EmojiPicker } from './Emojis/EmojiPicker'
import { EmojiSearch } from './Emojis/EmojiSearch'
import { replaceEmojis } from './Emojis/replaceEmojis'
import SvgCheck from '@jotta/ui/SvgCheck'

export class TextareaStore {
  private _text: string

  public get text() {
    return this._text
  }

  public set text(text: string) {
    this._text = text
  }

  constructor(text: string = '') {
    this._text = text
    makeAutoObservable(this)
  }
}

export type TextareaRef = { focus: () => void }

type TextareaProps = Partial<BoxProps> & {
  onSubmit?: (text: string) => void
  placeholder?: string
  textStore?: TextareaStore
  emoji?: boolean
  disabled?: boolean
  focusOnMount?: boolean
  className?: string
}

export const Textarea = observer(
  forwardRef(function Textarea(
    {
      placeholder = '',
      onSubmit,
      textStore,
      disabled = false,
      focusOnMount = false,
      ...props
    }: TextareaProps,
    ref: ForwardedRef<TextareaRef>,
  ) {
    const emojiSearchRef = useRef<HTMLDivElement>(null)
    const localRef = useRef<HTMLTextAreaElement>(null)
    const [height, setHeight] = useState('0px')
    const [search, setSearch] = useState('')
    const [selectionRange, setSelectionRange] = useState<
      [number, number] | null
    >(null)
    const mounted = useRef(false)
    const form = useRef<HTMLFormElement>(null)
    const store = useMemo(() => textStore || new TextareaStore(), [textStore])
    const { text } = store
    const emojiSearchOpen = useRef(false)

    // Focus on input change
    useEffect(() => {
      if (
        (focusOnMount || mounted.current) &&
        localRef.current &&
        localRef.current !== document.activeElement
      ) {
        localRef.current.focus({ preventScroll: true })
      }
      mounted.current = true
    }, [text, focusOnMount])

    useImperativeHandle(ref, () => ({
      focus() {
        if (localRef.current) {
          localRef.current.focus({ preventScroll: true })
          localRef.current.parentElement?.parentElement?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'start',
          })
        }
      },
    }))

    const insertEmoji = (item: any) => {
      if (!localRef.current) {
        return
      }

      const el = localRef.current

      if (selectionRange) {
        el.setSelectionRange(...selectionRange)
        setSelectionRange(null)
      }

      const pos = [el.selectionStart, el.selectionEnd]

      store.text = `${replaceEmojis(store.text.substr(0, pos[0]))}${
        item.native
      }${store.text.substr(pos[1])}`

      setTimeout(
        () =>
          el.setSelectionRange(
            pos[0] + item.native.length,
            pos[0] + item.native.length,
          ),
        0,
      )
    }

    const onEmojiPickerOpenChange = useCallback((open: boolean) => {
      if (!open && localRef.current) {
        localRef.current.focus({ preventScroll: true })
      }
    }, [])

    const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> =
      useCallback(
        e => {
          switch (e.key) {
            case 'Escape':
              if (search) {
                e.stopPropagation()
                e.preventDefault()
                setSearch('')
              } else if (text) {
                store.text = ''
                e.preventDefault()
              }
              break

            case 'Enter':
              if (e.ctrlKey || e.shiftKey) {
                break
              }
              e.preventDefault()

              if (!emojiSearchOpen.current && text && form.current) {
                form.current.dispatchEvent(
                  new Event('submit', { cancelable: true, bubbles: true }),
                )
              }
          }
        },
        [text, search, store],
      )

    // Compute height
    useEffect(() => {
      if (!localRef || !localRef.current) {
        return
      }

      localRef.current.style.height = '0'
      setHeight(`${localRef.current.scrollHeight}px`)
      localRef.current.style.height = ''
    }, [text])

    const getSearchWord = useCallback(() => {
      const el = localRef.current

      if (!el) {
        return
      }

      const text = el.value
      const end = el.selectionStart

      setSearch('')
      setSelectionRange(null)

      let start = end
      for (
        ;
        start > 0 && text[start - 1] !== ' ' && text[start - 1] !== ':';
        start-- // eslint-disable-next-line no-empty
      ) {}

      if (start === 0 || text[start - 1] !== ':' || end - start < 2) {
        return
      }

      setSearch(text.substring(start, end))
      setSelectionRange([start - 1, end])
    }, [])

    const inputHandler: React.FormEventHandler<HTMLTextAreaElement> =
      useCallback(
        e => {
          store.text = replaceEmojis(e.currentTarget.value)
          getSearchWord()
        },
        [getSearchWord, store],
      )

    const handleSubmit = useCallback<React.FormEventHandler>(
      e => {
        e.preventDefault()
        if (onSubmit) {
          onSubmit(store.text)
        }
        store.text = ''
      },
      [onSubmit, store],
    )

    const handleEmojiSearchOpenChange = useCallback((open: boolean) => {
      emojiSearchOpen.current = open

      if (!open) {
        setSearch('')
      }
    }, [])

    const id = useId()

    return (
      <form
        ref={form}
        {...props}
        onSubmit={handleSubmit}
        sx={{ display: 'flex', alignItems: 'center' }}
      >
        <EmojiSearch
          search={search}
          onSubmit={emoji => insertEmoji(emoji.skins[0])}
          onOpenChange={handleEmojiSearchOpenChange}
          ref={emojiSearchRef}
        >
          <BrandTextarea
            ref={localRef}
            id={id}
            placeholder={placeholder}
            onKeyDown={handleKeyDown}
            onBlur={useCallback((e: React.FocusEvent) => {
              if (
                e.relatedTarget &&
                emojiSearchRef.current &&
                e.relatedTarget.contains(emojiSearchRef.current)
              ) {
                e.preventDefault()
                localRef.current?.focus({ preventScroll: true })
                return
              }
              setSearch('')
            }, [])}
            sx={{
              height,
              color: 'inherit',
              '&:focus-visible': {
                boxShadow: 'none',
              },
              ':disabled': {
                backgroundColor: 'transparent',
                color: 'inherit',
              },
              fontFamily: 'body',
              fontWeight: 'light',
              padding: 0,
              '::placeholder': {
                opacity: 0.7,
                color: 'inherit',
                whiteSpace: 'nowrap',
              },
            }}
            value={text}
            onInput={inputHandler}
            disabled={disabled}
            {...props}
          />
        </EmojiSearch>
        <EmojiPicker
          onOpenChange={onEmojiPickerOpenChange}
          onSelect={insertEmoji}
          sx={{ ml: 2 }}
          disabled={disabled}
        />
        <button
          disabled={!text}
          type="submit"
          sx={{
            flex: '0 0 auto',
            ml: 2,
            color: 'hsl(120, 100%, 35%)',
            ':hover': { color: 'hsl(120, 100%, 40%)' },
            ':disabled': { display: 'none' },
            '& svg': {
              width: '24px',
              height: '24px',
              display: 'block',
            },
          }}
        >
          <PlainBrandIcon icon={SvgCheck} />
        </button>
      </form>
    )
  }),
)
